import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { WagmiProvider } from 'wagmi';
import { mainnet, solana } from '@reown/appkit/networks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SolanaAdapter } from '@reown/appkit-adapter-solana/react';
import React, { useMemo } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { clusterApiUrl } from '@solana/web3.js';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
// Import wallet adapter styles
import '@solana/wallet-adapter-react-ui/styles.css';

const projectId = `${process.env.REACT_APP_WALLETCONNECT_API_KEY}`;
if (!projectId) throw new Error('Project Id is not defined.');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Create wallet adapters

const wagmiAdapter = new WagmiAdapter({
  networks: [mainnet],
  projectId,
  ssr: true,
});

const solanaAdapter = new SolanaAdapter({});

createAppKit({
  adapters: [wagmiAdapter, solanaAdapter],
  networks: [mainnet, solana],
  projectId,
  metadata: {
    name: 'ColorFuel',
    description: 'ColorFuel Web3 Connect',
    url: 'https://colorfuel.art',
    icons: ['https://colorfuel.art/logo192.png']
  },
  features: {
    analytics: true,
    email: false,
    socials: []
  }
});

export const AppKitProvider = ({ children }: { children: React.ReactNode }) => {
  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ConnectionProvider endpoint={endpoint}>
          <WalletProvider 
            wallets={[]} 
            autoConnect={false}
            localStorageKey="colorfuel-wallet"
          >
            <WalletModalProvider className="wallet-modal-provider">
              {children}
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};
